import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles } from 'lucide-react';
import { OptimizeButton } from './components/OptimizeButton';
import { supabaseClient } from '../../../lib/supabase';
import { LoadingScreen } from '../../common/LoadingScreen';
import { parseUrlForEditor } from '../../../lib/editor-parser';
import Editor from './components/Editor';
import { LeftSidebar } from './components/LeftSidebar';
import RightSidebar from './components/RightSidebar';
import { HeaderStructure } from './components/HeaderStructure';
import { Metrics } from './types';
import { checkUniqueness } from '../../../lib/candy-content';
import { toast } from '../../../lib/toast';

const defaultMetrics: Metrics = {
  wordCount: { current: 0, avg: 0, max: 0 },
  entityDensity: { current: 0, avg: 0, max: 0 },
  headers: {
    h2: { current: 0, avg: 0, max: 0 },
    h3: { current: 0, avg: 0, max: 0 },
    h4: { current: 0, avg: 0, max: 0 }
  }
};


export function TextEditor() {
  // Ref to trigger metrics recalc in LeftSidebar
  const metricsSidebarRef = useRef<{ recalcMetrics: () => void } | null>(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [uniquenessScore, setUniquenessScore] = useState<number>(0);
  const [isRefreshingUniqueness, setIsRefreshingUniqueness] = useState(false);
  const [uniquenessUrl, setUniquenessUrl] = useState<string | null>(null);
  const [uniquenessMatches, setUniquenessMatches] = useState<Record<string, string>>({});
  const [uniquenessIgnored, setUniquenessIgnored] = useState<string[]>([]);
  const [uniquenessReportUrl, setUniquenessReportUrl] = useState<string | null>(null);
  const [competitorTexts, setCompetitorTexts] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'keywords' | 'recommendations' | 'competitors' | 'ai' | 'headers'>('keywords');
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [url, setUrl] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [metrics, setMetrics] = useState<Metrics>(defaultMetrics);
  const [editor, setEditor] = useState<any | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [editorKey, setEditorKey] = useState(0);
  const [contentAnalysis, setContentAnalysis] = useState<any>(null);
  const [keywordUpdateTrigger, setKeywordUpdateTrigger] = useState(0);
  const [textScore, setTextScore] = useState<number>(0);
  const [isEditorEnabled, setIsEditorEnabled] = useState(true);
  const [projectId, setProjectId] = useState<string | null>(null);

  const editorContentRef = useRef<string>('');
  const editorInitializedRef = useRef<boolean>(false);
  const [quillInstance, setQuillInstance] = useState<any>(null);

  const calculateCompetitorMetrics = useCallback((contentAnalysis: any) => {
    if (!contentAnalysis?.competitors) return;

    try {
      const competitors = contentAnalysis.competitors || [];
      const selectedDomains = contentAnalysis.selectedDomains || [];
      
      const selectedCompetitors = competitors.filter((comp: any) => 
        comp && comp.domain && selectedDomains.includes(comp.domain)
      );
      
      if (selectedCompetitors.length === 0) return;
      
      const wordCounts = selectedCompetitors.map((comp: any) => comp.word_count || 0);
      const avgWordCount = Math.round(wordCounts.reduce((sum: number, count: number) => sum + count, 0) / wordCounts.length);
      const maxWordCount = Math.max(...wordCounts);
      
      const h2Counts = selectedCompetitors.map((comp: any) => comp.headers?.h2?.length || 0);
      const h3Counts = selectedCompetitors.map((comp: any) => comp.headers?.h3?.length || 0);
      const h4Counts = selectedCompetitors.map((comp: any) => comp.headers?.h4?.length || 0);
      
      const avgH2Count = Math.round(h2Counts.reduce((sum: number, count: number) => sum + count, 0) / h2Counts.length);
      const avgH3Count = Math.round(h3Counts.reduce((sum: number, count: number) => sum + count, 0) / h3Counts.length);
      const avgH4Count = Math.round(h4Counts.reduce((sum: number, count: number) => sum + count, 0) / h4Counts.length);
      
      const maxH2Count = Math.max(...h2Counts);
      const maxH3Count = Math.max(...h3Counts);
      const maxH4Count = Math.max(...h4Counts);
      
      setMetrics({
        wordCount: { 
          current: 0,
          avg: avgWordCount,
          max: maxWordCount
        },
        entityDensity: { current: 0, avg: 0, max: 0 },
        headers: {
          h2: { current: 0, avg: avgH2Count, max: maxH2Count },
          h3: { current: 0, avg: avgH3Count, max: maxH3Count },
          h4: { current: 0, avg: avgH4Count, max: maxH4Count }
        }
      });
    } catch (err) {
      console.error('Error calculating competitor metrics:', err);
    }
  }, []);

  const handleContentChange = useCallback((newContent: string) => {
    editorContentRef.current = newContent;
    setContent(newContent);
    setSaveStatus('idle');
    // Trigger keyword update when content changes
    setKeywordUpdateTrigger(prev => prev + 1);
  }, []);

  // Callback to trigger metrics recalc
  const handleContentSet = useCallback(() => {
    console.log('[TextEditor] handleContentSet called', {
      hasMetricsSidebarRef: !!metricsSidebarRef.current,
      hasRecalcMetrics: !!(metricsSidebarRef.current?.recalcMetrics)
    });
    
    if (metricsSidebarRef.current && typeof metricsSidebarRef.current.recalcMetrics === 'function') {
      metricsSidebarRef.current.recalcMetrics();
    } else {
      console.log('[TextEditor] Cannot call recalcMetrics - ref not ready');
    }
  }, []);

  const handleRefreshUniqueness = useCallback(async () => {
    console.log('handleRefreshUniqueness called in editor/TextEditor');
    
    if (!quillInstance) {
      console.log('No quill instance, aborting');
      return;
    }
    
    setIsRefreshingUniqueness(true);
    setError(null);
    
    try {
      const text = quillInstance.getText();
      console.log('Text length:', text.trim().length);
      console.log('Starting uniqueness check...');
      
      const result = await checkUniqueness(text, url);
      console.log('Uniqueness result received:', result);
      
      setUniquenessScore(result.percentage);
      setUniquenessUrl(result.url || null);
      setUniquenessMatches(result.matches || {});
      setUniquenessIgnored(result.ignored || []);
      
      // Generate CandyContent report URL if we have details
      let reportUrl = null;
      if (result.details) {
        // Try to find UUID or task ID in the details
        const details = result.details;
        const uuid = details.uuid || details.task_id || details.id || details.check_id;
        if (uuid) {
          reportUrl = `https://candy-content.com/uniq-result/${uuid}/`;
          setUniquenessReportUrl(reportUrl);
          console.log('Generated CandyContent report URL:', reportUrl);
        }
      }
      
      // Save to database
      if (id) {
        const { error: updateError } = await supabaseClient
          .from('analyses')
          .update({
            uniqueness_score: result.percentage,
            uniqueness_url: result.url || null,
            uniqueness_matches: result.matches || {},
            uniqueness_ignored: result.ignored || [],
            uniqueness_checked_at: new Date().toISOString()
          })
          .eq('id', id);
        
        if (updateError) {
          console.error('Error saving uniqueness to database:', updateError);
        } else {
          console.log('Uniqueness saved to database');
        }
      }
    } catch (err) {
      console.error('Error checking uniqueness:', err);
      setError(err instanceof Error ? err.message : 'Failed to check uniqueness');
    } finally {
      setIsRefreshingUniqueness(false);
    }
  }, [quillInstance, url, id]);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabaseClient
          .from('analyses')
          .select('keyword, url, editor_content, content_analysis, uniqueness_score, uniqueness_url, uniqueness_matches, uniqueness_ignored, project_id, text_score')
          .eq('id', id)
          .single();

        if (error) throw error;

        console.log('Loaded analysis data:', { keyword: data.keyword, project_id: data.project_id });

        setKeyword(data.keyword || '');
        setUrl(data.url || '');
        setProjectId(data.project_id || null);
        setContentAnalysis(data.content_analysis || null);

        // Load text score if exists
        if (data.text_score !== null && data.text_score !== undefined) {
          setTextScore(data.text_score);
        }

        // Load uniqueness data if exists
        if (data.uniqueness_score !== null && data.uniqueness_score !== undefined) {
          setUniquenessScore(data.uniqueness_score);
        }
        if (data.uniqueness_url) {
          setUniquenessUrl(data.uniqueness_url);
        }
        if (data.uniqueness_matches) {
          setUniquenessMatches(data.uniqueness_matches);
        }
        if (data.uniqueness_ignored) {
          setUniquenessIgnored(data.uniqueness_ignored);
        }

        if (data.editor_content) {
          console.log('Loading editor content from database');
          setContent(data.editor_content);
          editorContentRef.current = data.editor_content;
          editorInitializedRef.current = true;
        }

        if (data.content_analysis) {
          calculateCompetitorMetrics(data.content_analysis);
        }
      } catch (err) {
        console.error('Error loading content:', err);
        setError(err instanceof Error ? err.message : 'Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [id, calculateCompetitorMetrics]);

  if (loading) return <LoadingScreen />;

  const saveTextScore = async (score: number) => {
    if (!id) return;
    
    try {
      const { error } = await supabaseClient
        .from('analyses')
        .update({ 
          text_score: score,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      setTextScore(score);
    } catch (err) {
      console.error('Error saving text score:', err);
    }
  };

  const handleSave = async () => {
    let contentToSave = '';
    
    if (quillInstance) {
      contentToSave = quillInstance.root.innerHTML;
    } else {
      contentToSave = editorContentRef.current;
    }
    
    if (!contentToSave.trim()) {
      setError("Cannot save empty content");
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setError(null);

    try {
      // Calculate current text score based on editor metrics
      const currentTextScore = textScore || 0;
      
      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({
          editor_content: contentToSave,
          text_score: currentTextScore,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      editorContentRef.current = contentToSave;
      setSaveStatus('saved');
      setKeywordUpdateTrigger(prev => prev + 1);

      toast.success('Content saved successfully!');

      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);

    } catch (err) {
      console.error('Error saving content:', err);
      setError(err instanceof Error ? err.message : 'Failed to save content');
      setSaveStatus('error');
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleParseContent = async () => {
    if (!url.trim()) {
      setError("Please enter a URL to parse");
      return;
    }

    setIsParsing(true);
    setError(null);
    setIsEditorEnabled(false);

    try {
      const result = await parseUrlForEditor(url);
      
      if (!result.content) {
        throw new Error('No content returned from parser');
      }

      console.log('Parsed content:', result.content.substring(0, 100) + '...');

      setContent(result.content);
      editorContentRef.current = result.content;
      editorInitializedRef.current = true;

      const { error: updateError } = await supabaseClient
        .from('analyses')
        .update({
          editor_content: result.content,
          url: url,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setKeywordUpdateTrigger(prev => prev + 1);
      toast.success('Content parsed and saved successfully!');
      setEditorKey(prev => prev + 1);

    } catch (err) {
      console.error('Error parsing content:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse content');
      toast.error('Failed to parse content');
    } finally {
      setIsParsing(false);
      setIsEditorEnabled(true);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              console.log('Back button clicked, projectId:', projectId);
              if (projectId) {
                console.log('Navigating to project:', `/projects/show/${projectId}`);
                navigate(`/projects/show/${projectId}`);
              } else {
                console.log('No projectId, navigating back');
                navigate(-1);
              }
            }}
            className="text-gray-500 hover:text-gray-700 cursor-pointer focus:outline-none transition-colors"
            type="button"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900">
            {keyword}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {id && <OptimizeButton analysisId={id} />}
          <button
            onClick={() => navigate(`/analyses/${id}/ai`)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Sparkles className="h-4 w-4 mr-2" />
            AI Assistant
          </button>
          <button
            onClick={handleSave}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
              saveStatus === 'saving' ? 'bg-indigo-400' : 
              saveStatus === 'saved' ? 'bg-green-600' : 
              saveStatus === 'error' ? 'bg-red-600' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            <Save className="h-4 w-4 mr-2" />
            {saveStatus === 'saving' ? 'Saving...' : 
             saveStatus === 'saved' ? 'Saved!' : 
             saveStatus === 'error' ? 'Retry' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <LeftSidebar
          ref={metricsSidebarRef}
          metrics={metrics}
          uniquenessScore={uniquenessScore}
          isRefreshingUniqueness={isRefreshingUniqueness}
          onRefreshUniqueness={handleRefreshUniqueness}
          uniquenessUrl={uniquenessUrl}
          uniquenessMatches={uniquenessMatches}
          uniquenessIgnored={uniquenessIgnored}
          uniquenessReportUrl={uniquenessReportUrl}
          quillInstance={quillInstance}
          analysisId={id}
          onTextScoreChange={saveTextScore}
        />

        <div className="flex-1 flex flex-col">
          <Editor
            key={editorKey}
            content={content}
            url={url}
            isParsing={isParsing}
            error={error}
            onContentChange={handleContentChange}
            onUrlChange={setUrl}
            onParseContent={handleParseContent}
            isEnabled={isEditorEnabled}
            isInitialized={editorInitializedRef.current}
            setQuillInstance={setQuillInstance}
            onContentSet={handleContentSet}
          />
        </div>

        <RightSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          analysisId={id}
          updateTrigger={keywordUpdateTrigger}
          quillInstance={quillInstance}
        />
      </div>
    </div>
  );
}